import {
    FetchState,
    PlanType,
    UserPlanState,
    UserPlanType,
    UserCoverageLevels
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: UserPlanState = {
    userPlan: {
        planType: PlanType.None,
        planTypeChangeEffectiveDate: null,
        isEditableByUser: false
    },
    fetchState: FetchState.Uninitialized,
    userCoverageLevels: []
};

export const useUserPlanStore = defineStore("userPlan", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("userPlan"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        canShowPlan: state => state.fetchState === FetchState.Loaded && state.userPlan.isEditableByUser,
        planTypePendingChange: state => {
            return state.userPlan.planTypeChangeEffectiveDate !== null && state.fetchState === FetchState.Loaded &&
                moment().isBefore(moment(state.userPlan.planTypeChangeEffectiveDate));
        },
        planType: state => state.userPlan.planType
    },
    actions: {
        setUserPlan (userPlanType: UserPlanType) {
            this.userPlan = userPlanType;
        },
        setUserPlanType (planType) {
            this.userPlan.planType = planType;
        },
        setAccountTypeEffectiveDate (planTypeChangeEffectiveDate) {
            this.userPlan.planTypeChangeEffectiveDate = planTypeChangeEffectiveDate;
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        getPlanType (): Promise<void> {
            return UserService.getPlanType()
                .then(response => {
                    const details = response.data;
                    this.setUserPlan(details);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        updatePlanType (args: {
            planType: any,
            planTypeChangeEffectiveDate: string
        }): Promise<void> {
            return UserService.updatePlanType(args)
                .then(response => {
                    this.setUserPlanType(args.planType);
                    this.setAccountTypeEffectiveDate(args.planTypeChangeEffectiveDate);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getUserCoverageLevels () {
            UserService.getUserCoverageLevels()
                .then(response => {
                    this.userCoverageLevels = response.data.coverageLevels;
                })
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        updateUserCoverageLevels (request: UserCoverageLevels) {
            return UserService.updateUserCoverageLevels(request)
                .then(response => {
                    const activeCoverageLevel = response.data.activeCoverageLevel;
                    this.setUserPlanType(activeCoverageLevel.coverageLevel);
                    this.setAccountTypeEffectiveDate(activeCoverageLevel.coverageLevelEffectiveDate);
                    this.userCoverageLevels = response.data.coverageLevels;

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Coverage Level successfully updated.");
                })
                .catch(error => storeHelper.handleError({ error, rethrow: true }));
        }
    }
});
